import { Button, Text, HbTooltip, useDisclosure } from '@cdl/heartbeat-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { MissingNoiseAnnotationsModal } from './MissingNoiseAnnotationsModal';
import { cleanUp } from '../thunks/clean-up.thunk';
import { trackEcg } from '../../analytics/thunks/track-ecg.thunk';
import { CLICK_NEXT } from '../../analytics/analytics.constants';
import { allowedWavesSelector } from '../../auth/selectors/auth.selectors';
import {
  allowNextNavigation,
  changesMadeSelector,
} from '../../delineation/selectors/delineation.selectors';
import { ecgCounterSelector } from '../selectors/ecg-count.selectors';
import {
  isAllLeadsNoiseAnnotatedSelector,
  noiseWaveListForTagSelector,
} from '../../wave/selectors/wave.selectors';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import { IncorrectOptionalNoiseTagModal } from './IncorrectOptionalNoiseTagModal';
import { getOptionalTagContainers } from '../../wave/utils/tags.utils';
import { getWavesFailingOptionalTagRequirement } from '../../wave/utils/wave.util';

const uncertainOptionalTagList = getOptionalTagContainers('uncertain', [
  'uncertain',
]).map((tag) => tag.tags[0]);
const microvoltedOptionalTagList = getOptionalTagContainers('microvolted', [
  'microvolted',
]).map((tag) => tag.tags[0]);

export const Next = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    isOpen: saveModalIsOpen,
    onOpen: saveModalOnOpen,
    onClose: saveModalOnClose,
  } = useDisclosure();
  const {
    isOpen: missingNoiseIsOpen,
    onOpen: missingNoiseOnOpen,
    onClose: missingNoiseOnClose,
  } = useDisclosure();

  const {
    isOpen: optionalNoiseTagIsOpen,
    onOpen: optionalNoiseTagOnOpen,
    onClose: optionalNoiseTagOnClose,
  } = useDisclosure();
  const changesMade = useSelector(changesMadeSelector);
  const ecgCount = useSelector(ecgCounterSelector);
  const allowNext = useSelector(allowNextNavigation);
  const allowedWaveToAnnotate = useSelector(
    allowedWavesSelector('wavesToAnnotate'),
  );
  const isAllLeadsNoiseAnnotated = useSelector(
    isAllLeadsNoiseAnnotatedSelector,
  );

  const uncertainWaves = useSelector(noiseWaveListForTagSelector('uncertain'));
  const microvoltedWaves = useSelector(
    noiseWaveListForTagSelector('microvolted'),
  );
  const incorrectUncertainWaves = getWavesFailingOptionalTagRequirement(
    uncertainWaves,
    uncertainOptionalTagList,
    1,
  );
  const incorrectMicrovoltedWaves = getWavesFailingOptionalTagRequirement(
    microvoltedWaves,
    microvoltedOptionalTagList,
  );

  const handleClick = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    if (allowedWaveToAnnotate.has('INV')) {
      if (
        incorrectUncertainWaves.length > 0 ||
        incorrectMicrovoltedWaves.length > 0
      ) {
        optionalNoiseTagOnOpen();
        return;
      }

      if (!isAllLeadsNoiseAnnotated) {
        missingNoiseOnOpen();
        return;
      }
    }

    if (changesMade) {
      saveModalOnOpen();
      return;
    }

    processNext();
  };

  const processNext = async () => {
    await dispatch(cleanUp());
    dispatch(trackEcg(CLICK_NEXT));
    navigate('/');
  };

  return (
    <>
      <HbTooltip
        hasArrow
        label={
          <>
            <Text>
              {ecgCount} {ecgCount === 1 ? 'diagnostic' : 'diagnostics'} saved
            </Text>
            <Text>since last login</Text>
          </>
        }
      >
        <Button
          size='s'
          variant='quaternary'
          data-testid='next-btn'
          isDisabled={!allowNext}
          onClick={handleClick}
        >
          Next
        </Button>
      </HbTooltip>
      <IncorrectOptionalNoiseTagModal
        isOpen={optionalNoiseTagIsOpen}
        onClose={optionalNoiseTagOnClose}
        uncertainWaveCount={incorrectUncertainWaves.length}
        microvoltedWaveCount={incorrectMicrovoltedWaves.length}
      />
      <MissingNoiseAnnotationsModal
        isOpen={missingNoiseIsOpen}
        onClose={missingNoiseOnClose}
        onConfirm={processNext}
      />
      <UnsavedChangesModal
        isOpen={saveModalIsOpen}
        onClose={saveModalOnClose}
        onConfirm={processNext}
      />
    </>
  );
};
